import type { CharacterSheet } from './domain.js';

/**
 * Single source of truth for sheet ownership / mutation rights, shared by the
 * server (command handlers) and the client (UI gating). Keeping this in one
 * place prevents the class of bug where a client-side permission check drifts
 * from the server's — e.g. the client optimistically routes a mutation the
 * server then rejects, leaving a phantom local change with no correction.
 */

type OwnableSheet = Pick<CharacterSheet, 'ownerName' | 'ownerId' | 'sheetType'>;

/** userId-first ownership: prefer stable ids, fall back to display name. */
export function isSheetOwner(
  sheet: Pick<CharacterSheet, 'ownerName' | 'ownerId'>,
  participantName: string,
  userId?: string,
): boolean {
  if (userId && sheet.ownerId) {
    return sheet.ownerId === userId;
  }
  return sheet.ownerName === participantName;
}

/** GM may mutate any sheet; a player may mutate only their own PC sheet. */
export function canModifySheet(
  sheet: OwnableSheet,
  role: 'gm' | 'player',
  participantName: string,
  userId?: string,
): boolean {
  if (role === 'gm') return true;
  return sheet.sheetType === 'pc' && isSheetOwner(sheet, participantName, userId);
}
